/** Valor gravado em `leads.produto` para novos leads da página Carregadores (wallbox). */
export const PRODUTO_LEAD_WALLBOX = "Instalação de Wallbox" as const;

/** Estudo de viabilidade para infraestrutura de recarga (EV) — página Carregadores. */
export const PRODUTO_LEAD_ESTUDO_VIABILIDADE_EV = "Estudo de Viabilidade - EV" as const;

/** Implantação de sistema de recarga compartilhada (SAVE) em condomínios — página Carregadores. */
export const PRODUTO_LEAD_IMPLANTACAO_SAVE_CONDOMINIOS =
  "Implantação de SAVE para Condomínios" as const;

/** Projeto e aprovação de SAVE (Celesc + CBMSC) — página Carregadores. */
export const PRODUTO_LEAD_PROJETO_APROVACAO_SAVE =
  "Projeto e Aprovação de SAVE" as const;

/** Instalação de wallbox em condomínio, com tabela de intervalos de distância. */
export const PRODUTO_LEAD_WALLBOX_CONDOMINIO =
  "Instalação de Wallbox - Condomínio" as const;

/** FV autossuficiente (zero-grid): mesmo funil de dimensionamento que on-grid; regras próprias em proposta/análise. */
export const PRODUTO_LEAD_SOLAR_ZERO_GRID = "Sistema Solar Zero-Grid" as const;

/** Legado — leads criados antes da renomeação do produto. */
export const PRODUTO_LEAD_CARREGADOR_LEGADO = "Carregadores Veiculares" as const;

/** Registro exclusivo do funil CRM de prospecção de parcerias (não é venda de kit). */
export const PRODUTO_LEAD_PARCERIA_CRM = "Prospecção Parceiro Sun7" as const;

/** Lead capturado sem linha de produto definida — só na 1ª etapa até escolher produto real. */
export const PRODUTO_LEAD_SEM_PRODUTO = "Sem Produto" as const;

/** Legado — valor único antes da separação FV vs elétricas. */
export const PRODUTO_LEAD_ADEQUACOES_REFORMAS = "Adequações e Reformas" as const;

/** Adequações e reformas em sistemas fotovoltaicos — página dedicada e CRM vendas. */
export const PRODUTO_LEAD_ADEQUACOES_REFORMAS_FV = "Adequações e Reformas FV" as const;

/** Adequações e reformas elétricas / prediais — página dedicada e CRM vendas. */
export const PRODUTO_LEAD_ADEQUACOES_REFORMAS_ELETRICAS =
  "Adequações e Reformas Elétricas" as const;

/** Sistema de alarme de incêndio (central, acionadores, sirenes e avisadores). */
export const PRODUTO_LEAD_SISTEMA_ALARME_INCENDIO =
  "Sistema de Alarme de Incêndio" as const;

/** Sistema de detecção automática de incêndio (SDAI), com detectores de fumaça. */
export const PRODUTO_LEAD_SISTEMA_DETCCAO_AUTOMATICA_INCENDIO =
  "Sistema de Detecção Automática de Incêndio" as const;

export const PRODUTOS_LEAD_INCENDIO = [
  PRODUTO_LEAD_SISTEMA_ALARME_INCENDIO,
  PRODUTO_LEAD_SISTEMA_DETCCAO_AUTOMATICA_INCENDIO,
] as const;

/** Todos os valores de produto desta linha (inclui legado para consultas e migração gradual). */
export const PRODUTOS_LEAD_ADEQUACOES_REFORMAS = [
  PRODUTO_LEAD_ADEQUACOES_REFORMAS_FV,
  PRODUTO_LEAD_ADEQUACOES_REFORMAS_ELETRICAS,
  PRODUTO_LEAD_ADEQUACOES_REFORMAS,
  ...PRODUTOS_LEAD_INCENDIO,
] as const;

/** Valores de `leads.produto` usados no cadastro (alinhado ao formulário de lead). */
export const LEAD_PRODUTO_VALORES = [
  "Sistema Solar (on-grid)",
  "Sistema Solar Híbrido (com bateria)",
  PRODUTO_LEAD_SOLAR_ZERO_GRID,
  PRODUTO_LEAD_WALLBOX,
  PRODUTO_LEAD_WALLBOX_CONDOMINIO,
  PRODUTO_LEAD_ESTUDO_VIABILIDADE_EV,
  PRODUTO_LEAD_IMPLANTACAO_SAVE_CONDOMINIOS,
  PRODUTO_LEAD_PROJETO_APROVACAO_SAVE,
  "Kit Anti-Apagão",
  PRODUTO_LEAD_PARCERIA_CRM,
  PRODUTO_LEAD_ADEQUACOES_REFORMAS_FV,
  PRODUTO_LEAD_ADEQUACOES_REFORMAS_ELETRICAS,
  PRODUTO_LEAD_SISTEMA_ALARME_INCENDIO,
  PRODUTO_LEAD_SISTEMA_DETCCAO_AUTOMATICA_INCENDIO,
  PRODUTO_LEAD_SEM_PRODUTO,
] as const;

/** Valores exibidos no cadastro + legado ainda possível em `leads.produto`. */
export type LeadProdutoValor =
  | (typeof LEAD_PRODUTO_VALORES)[number]
  | typeof PRODUTO_LEAD_CARREGADOR_LEGADO;

export type LeadProdutoBadgeMeta = {
  label: string;
  /** Classes extras no `Badge` (variant outline) — ex.: card do funil CRM. */
  badgeClassName: string;
};

/** Mesma família cromática no funil: FV (verde) vs. carregadores (laranja). */
const BADGE_FOTOVOLTAICO =
  "border-emerald-500/90 bg-emerald-50/95 text-emerald-950 shadow-sm shadow-emerald-900/5";
const BADGE_CARREGADOR =
  "border-orange-500/90 bg-orange-50/95 text-orange-950 shadow-sm shadow-orange-900/5";
const BADGE_ADEQUACOES_REFORMAS =
  "border-slate-500/80 bg-slate-50/95 text-slate-900 shadow-sm shadow-slate-900/10";
const BADGE_INCENDIO =
  "border-red-500/80 bg-red-50/95 text-red-950 shadow-sm shadow-red-900/10";

const LEAD_PRODUTO_BADGE_META: Record<LeadProdutoValor, LeadProdutoBadgeMeta> = {
  "Sistema Solar (on-grid)": {
    label: "Solar (on-grid)",
    badgeClassName: BADGE_FOTOVOLTAICO,
  },
  "Sistema Solar Híbrido (com bateria)": {
    label: "Solar (híbrido)",
    badgeClassName: BADGE_FOTOVOLTAICO,
  },
  [PRODUTO_LEAD_SOLAR_ZERO_GRID]: {
    label: "Solar (zero-grid)",
    badgeClassName: BADGE_FOTOVOLTAICO,
  },
  [PRODUTO_LEAD_CARREGADOR_LEGADO]: {
    label: "EV — legado",
    badgeClassName: BADGE_CARREGADOR,
  },
  [PRODUTO_LEAD_WALLBOX]: {
    label: "Wallbox",
    badgeClassName: BADGE_CARREGADOR,
  },
  [PRODUTO_LEAD_WALLBOX_CONDOMINIO]: {
    label: "Wallbox condomínio",
    badgeClassName: BADGE_CARREGADOR,
  },
  [PRODUTO_LEAD_ESTUDO_VIABILIDADE_EV]: {
    label: "Estudo EV",
    badgeClassName: BADGE_CARREGADOR,
  },
  [PRODUTO_LEAD_IMPLANTACAO_SAVE_CONDOMINIOS]: {
    label: "SAVE condomínios",
    badgeClassName: BADGE_CARREGADOR,
  },
  [PRODUTO_LEAD_PROJETO_APROVACAO_SAVE]: {
    label: "Projeto SAVE",
    badgeClassName: BADGE_CARREGADOR,
  },
  "Kit Anti-Apagão": {
    label: "Kit anti-apagão",
    badgeClassName: BADGE_FOTOVOLTAICO,
  },
  [PRODUTO_LEAD_PARCERIA_CRM]: {
    label: "Parceria CRM",
    badgeClassName: "border-violet-400 bg-violet-50/95 text-violet-950 shadow-sm shadow-violet-900/5",
  },
  [PRODUTO_LEAD_ADEQUACOES_REFORMAS_FV]: {
    label: "Ad. / reformas FV",
    badgeClassName: BADGE_ADEQUACOES_REFORMAS,
  },
  [PRODUTO_LEAD_ADEQUACOES_REFORMAS_ELETRICAS]: {
    label: "Ad. / reformas elétricas",
    badgeClassName: BADGE_ADEQUACOES_REFORMAS,
  },
  [PRODUTO_LEAD_SISTEMA_ALARME_INCENDIO]: {
    label: "Alarme de incêndio",
    badgeClassName: BADGE_INCENDIO,
  },
  [PRODUTO_LEAD_SISTEMA_DETCCAO_AUTOMATICA_INCENDIO]: {
    label: "SDAI",
    badgeClassName: BADGE_INCENDIO,
  },
  [PRODUTO_LEAD_SEM_PRODUTO]: {
    label: "Sem produto",
    badgeClassName: "border-amber-500/80 bg-amber-50/95 text-amber-950 shadow-sm shadow-amber-900/10",
  },
};

/** Rótulo e estilo do badge de linha de produto no CRM (cards do funil). */
export function leadProdutoBadgeMeta(
  produto: string | null | undefined
): LeadProdutoBadgeMeta | null {
  if (produto == null || produto === "") return null;
  const known = LEAD_PRODUTO_BADGE_META[produto as LeadProdutoValor];
  if (known) return known;
  return {
    label: produto,
    badgeClassName: "border-muted-foreground/50 bg-muted/40 text-muted-foreground",
  };
}

/** Nome do lead + produto, ex.: "João (Instalação de Wallbox)". */
export function formatLeadNomeComProduto(
  lead: {
    tipo?: string | null;
    nome?: string | null;
    razao_social?: string | null;
    razaoSocial?: string | null;
    produto?: string | null;
  } | null | undefined,
  options?: { useShortLabel?: boolean }
): string {
  if (!lead) return "—";
  const nomeBase =
    lead.tipo === "juridica"
      ? lead.razao_social || lead.razaoSocial || lead.nome || "—"
      : lead.nome || lead.razao_social || lead.razaoSocial || "—";
  const produto = lead.produto?.trim();
  if (!produto) return nomeBase;
  const label = options?.useShortLabel
    ? leadProdutoBadgeMeta(produto)?.label ?? produto
    : produto;
  return `${nomeBase} (${label})`;
}

/** Linha carregadores EV no cadastro atual (sem duplicar o legado “Carregadores Veiculares”). */
export const PRODUTOS_LEAD_CARREGADOR_SEM_LEGADO = [
  PRODUTO_LEAD_WALLBOX,
  PRODUTO_LEAD_WALLBOX_CONDOMINIO,
  PRODUTO_LEAD_ESTUDO_VIABILIDADE_EV,
  PRODUTO_LEAD_IMPLANTACAO_SAVE_CONDOMINIOS,
  PRODUTO_LEAD_PROJETO_APROVACAO_SAVE,
] as const;

/** Incluir todos em consultas `.in("produto", …)` e exclusões do funil solar (inclui legado). */
export const PRODUTOS_LEAD_CARREGADOR = [
  PRODUTO_LEAD_CARREGADOR_LEGADO,
  ...PRODUTOS_LEAD_CARREGADOR_SEM_LEGADO,
] as const;

export function isProdutoLeadCarregador(
  produto: string | null | undefined
): boolean {
  if (produto == null || produto === "") return false;
  return (PRODUTOS_LEAD_CARREGADOR as readonly string[]).includes(produto);
}

export function isProdutoLeadParceiroCrm(
  produto: string | null | undefined
): boolean {
  return produto === PRODUTO_LEAD_PARCERIA_CRM;
}

export function isProdutoLeadSemProduto(
  produto: string | null | undefined
): boolean {
  return produto === PRODUTO_LEAD_SEM_PRODUTO;
}

export function isProdutoEstudoViabilidadeEv(
  produto: string | null | undefined
): boolean {
  return produto === PRODUTO_LEAD_ESTUDO_VIABILIDADE_EV;
}

export function isProdutoImplantacaoSaveCondominios(
  produto: string | null | undefined
): boolean {
  return produto === PRODUTO_LEAD_IMPLANTACAO_SAVE_CONDOMINIOS;
}

export function isProdutoProjetoAprovacaoSave(
  produto: string | null | undefined
): boolean {
  return produto === PRODUTO_LEAD_PROJETO_APROVACAO_SAVE;
}

export function isProdutoInstalacaoWallboxCondominio(
  produto: string | null | undefined
): boolean {
  return produto === PRODUTO_LEAD_WALLBOX_CONDOMINIO;
}

/** Instalação residencial de wallbox (inclui o produto legado). */
export function isProdutoInstalacaoWallbox(
  produto: string | null | undefined
): boolean {
  return produto === PRODUTO_LEAD_WALLBOX || produto === PRODUTO_LEAD_CARREGADOR_LEGADO;
}

export function isProdutoLeadAdequacoesReformas(
  produto: string | null | undefined
): boolean {
  if (produto == null || produto === "") return false;
  return (PRODUTOS_LEAD_ADEQUACOES_REFORMAS as readonly string[]).includes(produto);
}

export function isProdutoLeadAdequacoesReformasFv(
  produto: string | null | undefined
): boolean {
  return produto === PRODUTO_LEAD_ADEQUACOES_REFORMAS_FV;
}

export function isProdutoLeadAlarmeIncendio(
  produto: string | null | undefined
): boolean {
  return produto === PRODUTO_LEAD_SISTEMA_ALARME_INCENDIO;
}

export function isProdutoLeadSdai(produto: string | null | undefined): boolean {
  return produto === PRODUTO_LEAD_SISTEMA_DETCCAO_AUTOMATICA_INCENDIO;
}

export function isProdutoLeadIncendio(produto: string | null | undefined): boolean {
  if (produto == null || produto === "") return false;
  return (PRODUTOS_LEAD_INCENDIO as readonly string[]).includes(produto);
}

/** Leads que não usam o dimensionador de kit solar (lista de cliente na etapa 1). */
export function isProdutoLeadExcluidoDoDimensionamentoFv(
  produto: string | null | undefined
): boolean {
  return (
    isProdutoLeadCarregador(produto) ||
    isProdutoLeadAdequacoesReformas(produto) ||
    isProdutoLeadSemProduto(produto)
  );
}

/** Excluir do funil “solar” agregado no dashboard (contagens por etapa). */
export function isProdutoLeadExcluidoFunilSolarDashboard(
  produto: string | null | undefined
): boolean {
  return isProdutoLeadCarregador(produto) || isProdutoLeadAdequacoesReformas(produto);
}

/** Mesmos campos e fluxo de dimensionamento que on-grid / híbrido (não é carregador). */
/** Linha de negócio para templates de tarefas (`tarefas_templates.linha_produto`). */
export type LinhaProdutoTarefas = "fotovoltaico" | "carregador_veicular";

export function linhaProdutoTarefasFromLeadProduto(
  produto: string | null | undefined
): LinhaProdutoTarefas {
  return isProdutoLeadCarregador(produto) ? "carregador_veicular" : "fotovoltaico";
}

export function isProdutoLeadSolarComoOnGrid(
  produto: string | null | undefined
): boolean {
  if (produto == null || produto === "") return false;
  return (
    produto === "Sistema Solar (on-grid)" ||
    produto === "Sistema Solar Híbrido (com bateria)" ||
    produto === PRODUTO_LEAD_SOLAR_ZERO_GRID
  );
}

/** Lead com sistema híbrido (FV + bateria) — dimensionamento e kit manual exigem bateria. */
export function isProdutoLeadSolarHibrido(produto: string | null | undefined): boolean {
  return produto === "Sistema Solar Híbrido (com bateria)";
}

/** Filtro PostgREST: `produto` nulo ou fora dos produtos de carregador/wallbox. */
export function supabaseOrProdutoNaoCarregador(): string {
  const quoted = PRODUTOS_LEAD_CARREGADOR.map((p) => `"${p.replace(/"/g, '\\"')}"`).join(",");
  return `produto.is.null,produto.not.in.(${quoted})`;
}
